import * as React from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import moment from "moment";
import { isEurope } from "../utils/isEurope";

type AddressProps = {
  userData: {
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    place_of_birth: string;
    nationality: string;
    country_residence: string;
    employment_title: string;
    employment_type: string;
    address1: string;
    city: string;
    state: string;
    country: string;
    department: string;
    token: string | null;
    company_id: string | null;
  };
  errors: {
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    place_of_birth: string;
    nationality: string;
    country_residence: string;
    employment_title: string;
    employment_type: string;
    address1: string;
    city: string;
    state: string;
    country: string;
  };
  setErrors: (value: any) => void;
  setUserData: (value: any) => void;
  steps: string[];
  activeStep: number;
  setActiveStep: (value: number) => void;
  countries: { data: string[] };
  defaultCountry: { country_name: string | null };
};

export default function Address({
  userData,
  errors,
  setErrors,
  setUserData,
  steps,
  activeStep,
  setActiveStep,
  countries,
  defaultCountry,
}: AddressProps) {
  const [open, setOpen] = React.useState(false);
  const [userError, setuserError] = React.useState("");

  // Set default country as United Arab Emirates if not already set
  React.useEffect(() => {
    if (!userData.country) {
      setUserData({
        ...userData,
        country: isEurope
          ? defaultCountry.country_name
          : "United Arab Emirates",
      });
    }
  }, [userData, setUserData, defaultCountry]);

  const handleNext = () => {
    if (userData.address1.trim() === "") {
      setErrors({ ...errors, address1: "This field required" });
    } else if (!/^[0-9a-zA-Z ]+$/.test(userData.address1)) {
      setErrors({
        ...errors,
        address1: "Should not contain special characters",
      });
    } else if (userData.city.trim() === "") {
      setErrors({ ...errors, city: "This field required" });
    } else if (!/^[a-zA-Z ]+$/.test(userData.city)) {
      setErrors({ ...errors, city: "Should not contain special characters" });
    } else if (userData.state.trim() === "") {
      setErrors({ ...errors, state: "This field required" });
    } else if (!/^[a-zA-Z ]+$/.test(userData.state)) {
      setErrors({ ...errors, state: "Should not contain special characters" });
    } else if (userData.country.trim() === "") {
      setErrors({ ...errors, country: "This field required" });
    } else {
      // eslint-disable-next-line no-param-reassign
      userData.date_of_birth = moment(userData.date_of_birth).format(
        "DD/MM/yyyy"
      );
      fetch(`${process.env.REACT_APP_SERVER_HOSTNAME}/complete_user_profile`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        mode: "cors",
        method: "POST",
        body: JSON.stringify(userData),
      })
        .then(async (response) => {
          const data = await response.json();
          if (response.status === 200) {
            setActiveStep(activeStep + 1);
          } else {
            setuserError(data.error);
            setOpen(true);
          }
        })
        .catch((error) => {
          setuserError(error);
          setOpen(true);
        });
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleChange = (name: string, value: string) => {
    setErrors({
      ...errors,
      address1: "",
      city: "",
      state: "",
      country: "",
    });
    setUserData({ ...userData, [name]: value });
  };

  return (
    <>
      <Stack sx={{ width: "100%" }} spacing={2}>
        <Collapse in={open}>
          <Alert severity="error">{userError}</Alert>
        </Collapse>
      </Stack>
      <Typography variant="h6" gutterBottom>
        Address
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="normal"
            required
            name="address1"
            label="Address line 1"
            id="address1"
            error={errors.address1 !== ""}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={userData.address1}
            helperText={errors.address1 !== "" ? errors.address1 : ""}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            name="city"
            label="City"
            id="city"
            error={errors.city !== ""}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={userData.city}
            helperText={errors.city !== "" ? errors.city : ""}
          />
        </Grid>

        {isEurope ? (
          <Grid item xs={12} sm={6}>
            <FormControl sx={{ m: 0, minWidth: "100%" }}>
              <InputLabel id="demo-simple-select-required-label">
                Country *
              </InputLabel>
              <Select
                required
                name="country"
                labelId="demo-simple-select-standard-label"
                id="country"
                style={{ width: 239 }}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                value={userData.country || defaultCountry.country_name}
                label="Country"
                error={errors.country !== ""}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {countries.data.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.country !== "" ? errors.country : ""}
              </FormHelperText>
            </FormControl>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <FormControl sx={{ m: 0, minWidth: "100%" }}>
              <InputLabel id="demo-simple-select-required-label">
                Country *
              </InputLabel>
              <Select
                required
                name="country"
                labelId="demo-simple-select-standard-label"
                id="country"
                style={{ width: 239 }}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                value={userData.country}
                label="Country"
                error={errors.country !== ""}
              >
                <MenuItem key={userData.country} value={userData.country}>
                  {userData.country}
                </MenuItem>
              </Select>
              <FormHelperText>
                {errors.country !== "" ? errors.country : ""}
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            required
            name="state"
            label="State / Province"
            id="state"
            error={errors.state !== ""}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={userData.state}
            helperText={errors.state !== "" ? errors.state : ""}
          />
        </Grid>
      </Grid>
      {activeStep === steps.length ? (
        <Typography variant="h4" gutterBottom>
          Thank you for your submission.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {activeStep !== 0 && (
            <Button onClick={handleBack} sx={{ mt: 3, ml: 1 }}>
              Back
            </Button>
          )}

          <Button
            name="next"
            variant="contained"
            onClick={handleNext}
            sx={{ mt: 3, ml: 1 }}
          >
            {activeStep === steps.length - 1 ? "Update" : "Next"}
          </Button>
        </Box>
      )}
    </>
  );
}
